import { clsx } from "clsx";
import {
  type FormHTMLAttributes,
  type ReactNode,
  type SubmitEventHandler,
  useEffect,
  useRef,
} from "react";

export type FormErrorValue = string | readonly string[] | null | undefined;
export type FormErrors = Readonly<Record<string, FormErrorValue>>;

/**
 * Return the first server or local validation message for equivalent field names.
 *
 * The backend's generated field keys remain authoritative. Aliases are explicit
 * at the call site so a renamed UI state key cannot silently discard an API
 * validation error.
 */
export function firstFormError(errors: FormErrors, ...names: string[]): string | undefined {
  for (const name of names) {
    const value = errors[name];
    if (typeof value === "string" && value) return value;
    if (Array.isArray(value) && typeof value[0] === "string" && value[0]) return value[0];
  }
  return undefined;
}

/**
 * The shared semantic shell for forms built from Field controls.
 *
 * Field owns labels and per-control `aria-invalid`/`aria-errormessage`; Form
 * standardises browser validation policy and exposes mutation state without
 * replacing generated API contracts with a second client schema.
 */
export function Form({
  pending = false,
  noValidate = true,
  focusErrorSignal,
  children,
  onSubmit,
  ...props
}: FormHTMLAttributes<HTMLFormElement> & {
  pending?: boolean;
  /** Change this value when local or server validation errors are published. */
  focusErrorSignal?: unknown;
  children: ReactNode;
}) {
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!focusErrorSignal) return;
    const frame = window.requestAnimationFrame(() => {
      const form = formRef.current;
      const invalid = form?.querySelector<HTMLElement>(
        '[aria-invalid="true"]:not([disabled]), [data-form-error-focus]:not([disabled])',
      );
      invalid?.focus({ preventScroll: false });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusErrorSignal]);

  const submit: SubmitEventHandler<HTMLFormElement> = (event) => {
    if (pending) {
      event.preventDefault();
      return;
    }
    onSubmit?.(event);
  };

  return (
    <form
      {...props}
      ref={formRef}
      noValidate={noValidate}
      aria-busy={pending || undefined}
      onSubmit={submit}
    >
      {children}
    </form>
  );
}

/** One consistent, announced home for a form-wide or transport error. */
export function FormError({ children, className }: { children?: ReactNode; className?: string }) {
  if (!children) return null;
  return (
    <p
      className={clsx(
        "rounded-md border border-critical/25 bg-critical-soft px-3 py-2 text-sm text-critical",
        className,
      )}
      role="alert"
      tabIndex={-1}
      data-form-error-focus
    >
      {children}
    </p>
  );
}
